import { useState, useMemo } from 'react';
import { NewPageFormState } from '../types';

interface StepValidation {
  isValid: boolean;
  canProceed: boolean;
  errors: string[];
}

interface StepNavigation {
  currentStep: number;
  totalSteps: number;
  canGoNext: boolean;
  canGoPrevious: boolean;
  canSubmit: boolean;
  goToNext: () => void;
  goToPrevious: () => void;
  goToStep: (step: number) => void;
  resetToFirstStep: () => void;
  getStepValidation: (step: number) => StepValidation;
}

interface UseStepNavigationProps {
  formState: NewPageFormState;
  canProceedWithCreation: boolean;
  onStepChange?: (step: number) => void;
}

export const useStepNavigation = ({
  formState,
  canProceedWithCreation,
  onStepChange
}: UseStepNavigationProps): StepNavigation => {
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 2; // TEMPORARILY REDUCED: Was 3, now 2 (Step 3 research hidden)

  // Step 1 validation: Basic page info (title, purpose, and type)
  const step1Validation = useMemo((): StepValidation => {
    const errors: string[] = [];
    
    if (formState.title.length < 3) {
      errors.push('Title must be at least 3 characters');
    }
    
    if (formState.initial_request.length < 20) {
      errors.push('Page description must be at least 20 characters');
    }
    
    if (!formState.type) {
      errors.push('Page type must be selected or auto-detected');
    }
    
    const isValid = formState.title.length >= 3 && 
                    formState.initial_request.length >= 20 &&
                    !!formState.type;
    
    return {
      isValid,
      canProceed: isValid,
      errors
    };
  }, [formState.title, formState.initial_request, formState.type]);

  // Step 2 validation: Clarifying questions (optional - no validation required)
  const step2Validation = useMemo((): StepValidation => {
    const errors: string[] = [];
    
    // Include step 1 validation
    if (!step1Validation.isValid) {
      errors.push(...step1Validation.errors);
    }
    
    // Clarifying questions are optional - always valid if step 1 is valid
    const isValid = step1Validation.isValid;
    
    return {
      isValid,
      canProceed: isValid,
      errors
    };
  }, [step1Validation]);

  // Step 3 validation: Clarifying questions (TEMPORARILY DISABLED)
  const step3Validation = useMemo((): StepValidation => {
    const errors: string[] = [];
    
    // Include previous step validations
    if (!step2Validation.isValid) {
      errors.push(...step2Validation.errors);
    }
    
    // No longer require answering clarifying questions
    const isValid = step2Validation.isValid;
    
    return {
      isValid,
      canProceed: isValid,
      errors
    };
  }, [step2Validation]); // KEEP FOR FUTURE RESTORATION

  const getStepValidation = (step: number): StepValidation => {
    switch (step) {
      case 1:
        return step1Validation;
      case 2:
        return step2Validation;
      // STEP 3 TEMPORARILY DISABLED
      // case 3:
      //   return step3Validation;
      default:
        return { isValid: false, canProceed: false, errors: ['Invalid step'] };
    }
  };

  const canGoNext = currentStep < totalSteps && getStepValidation(currentStep).canProceed;
  const canGoPrevious = currentStep > 1;
  const canSubmit = currentStep === totalSteps && step2Validation.isValid; // CHANGED: Use step2 instead of step3

  const goToNext = () => {
    console.log('[useStepNavigation] goToNext called', {
      currentStep,
      canGoNext,
      currentStepValid: getStepValidation(currentStep).isValid,
      currentStepErrors: getStepValidation(currentStep).errors,
      formState: {
        titleLength: formState.title.length,
        purposeLength: formState.initial_request.length,
        hasType: !!formState.type,
        type: formState.type,
        hasQuestions: !!formState.clarifyingQuestions?.questions?.length,
        answeredQuestions: formState.clarifyingQuestions?.answers?.filter(a => a.length >= 50).length || 0
      }
    });
    
    if (canGoNext) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      onStepChange?.(nextStep);
      console.log('[useStepNavigation] Moved to step', nextStep);
    } else {
      console.log('[useStepNavigation] Cannot go to next step', { canGoNext, validation: getStepValidation(currentStep) });
    }
  };

  const goToPrevious = () => {
    if (canGoPrevious) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      onStepChange?.(prevStep);
    }
  };

  const goToStep = (step: number) => {
    if (step >= 1 && step <= totalSteps) {
      setCurrentStep(step);
      onStepChange?.(step);
    }
  };

  const resetToFirstStep = () => {
    setCurrentStep(1);
    onStepChange?.(1);
  };

  return {
    currentStep,
    totalSteps,
    canGoNext,
    canGoPrevious,
    canSubmit,
    goToNext,
    goToPrevious,
    goToStep,
    resetToFirstStep,
    getStepValidation
  };
}; 